import { faApple, faGoogle } from '@fortawesome/free-brands-svg-icons';
import { faCreditCard, faQrcode } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AnimatePresence, motion } from 'motion/react';
import { useState } from 'react';

import { CryptoDepositScreen } from './CryptoDepositScreen';

interface DepositTrayProps {
  isOpen: boolean;
  onClose: () => void;
}

export const DepositTray = ({ isOpen, onClose }: DepositTrayProps) => {
  const [showCryptoScreen, setShowCryptoScreen] = useState(false);

  const depositOptions = [
    {
      id: 'crypto',
      name: 'Crypto',
      icon: <FontAwesomeIcon icon={faQrcode} fontSize={32} />,
      description: 'Receive USDC from a wallet',
    },
    {
      id: 'apple-pay',
      name: 'Apple Pay',
      icon: <FontAwesomeIcon icon={faApple} fontSize={32} />,
      description: 'Load up your account directly',
    },
    {
      id: 'google-pay',
      name: 'Google Pay',
      icon: <FontAwesomeIcon icon={faGoogle} fontSize={32} />,
      description: 'Load up your account directly',
    },
    {
      id: 'debit-card',
      name: 'Debit',
      icon: <FontAwesomeIcon icon={faCreditCard} fontSize={32} />,
      description: 'Deposit USDC with a debit card',
    },
  ];

  const handleOptionClick = (option: (typeof depositOptions)[0]) => {
    if (option.id === 'crypto') {
      setShowCryptoScreen(true);
    } else {
      console.log(`Selected deposit method: ${option.name}`);
      // Here you would handle the other deposit options
      onClose();
    }
  };

  const handleClose = () => {
    // Reset to initial state when closing the tray
    setShowCryptoScreen(false);
    onClose();
  };

  const handleBackToPicker = () => {
    setShowCryptoScreen(false);
  };

  return (
    <>
      {/* Backdrop */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="fixed inset-0 bg-brand-dark/80 z-40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={handleClose}
          />
        )}
      </AnimatePresence>

      {/* Tray */}
      <motion.div
        className="fixed bottom-0 left-0 right-0 z-50 bg-[#1a1a1a] rounded-t-3xl shadow-2xl"
        initial={{ y: '100%' }}
        animate={{ y: isOpen ? 0 : '100%' }}
        transition={{
          type: 'spring',
          damping: 30,
          stiffness: 300,
          duration: 0.4,
        }}
        style={{ pointerEvents: isOpen ? 'auto' : 'none' }}
      >
        {/* Tray Handle */}
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-12 h-1 bg-gray-600 rounded-full"></div>
        </div>

        {/* Tray Content */}
        {!showCryptoScreen ? (
          <div className="px-6 pb-8">
            {/* Deposit Options */}
            <div className="space-y-3">
              {depositOptions.map((option, index) => (
                <motion.button
                  key={option.id}
                  onClick={() => handleOptionClick(option)}
                  className="w-full flex items-center gap-4 p-2 rounded-lg bg-[#2a2a2a] hover:bg-[#333333] transition-colors text-left group"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{
                    opacity: isOpen ? 1 : 0,
                    y: isOpen ? 0 : 20,
                  }}
                  transition={{
                    delay: isOpen ? index * 0.1 + 0.1 : 0,
                    duration: 0.3,
                  }}
                >
                  {/* Icon */}
                  <div className="w-12 h-12 flex items-center justify-center text-xl text-white group-hover:text-brand-green transition-colors">
                    {option.icon}
                  </div>

                  {/* Text Content */}
                  <div className="flex-1">
                    <div className="font-['Inter:Medium',_sans-serif] font-medium text-[#ffffff] text-[18px]">
                      {option.name}
                    </div>
                    <div className="font-['Inter:Light',_sans-serif] font-light text-[#808080] text-[14px]">
                      {option.description}
                    </div>
                  </div>

                  {/* Arrow */}
                  <div className="text-gray-400 group-hover:text-brand-green transition-colors">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M9 18L15 12L9 6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                </motion.button>
              ))}
            </div>
          </div>
        ) : (
          <CryptoDepositScreen
            isOpen={showCryptoScreen}
            onClose={handleClose}
            onBack={handleBackToPicker}
          />
        )}
      </motion.div>
    </>
  );
};
